<template>
  <div>

    <Head :title="__('Customers')" />
    <div class="flex items-center justify-between mb-6">
      <search-input v-model="form.search" class="w-full max-w-md mr-4" @reset="reset"></search-input>
      <div>
        <Link class="mr-2 btn-indigo" :href="route('customers.import')">
        <span>{{ __('Import Student') }}</span>
        </Link>
        <Link class="btn-indigo" :href="route('customers.create')">
        <span>{{ __('Create New') }}</span>
        </Link>
      </div>
    </div>
    <div class="mb-4 overflow-x-auto">
      <nav class="flex border-b border-gray-200 min-w-max">
        <button v-for="tab in campusTabs" :key="tab.key" type="button" :class="campusTabClass(tab)"
          @click="filterByTab(tab)">
          {{ __(tab.label) }}
          <span :class="campusTabCountClass(tab)">{{ tab.count }}</span>
        </button>
      </nav>
    </div>
    <div class="overflow-x-auto bg-white rounded-md shadow">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="font-bold text-left">
            <th class="px-6 pt-6 pb-4">{{ __('Name') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('Email') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('Phone') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('City') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('Campus') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('Created') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users.data" :key="user.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="border-t">
              <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="route('customers.edit', user.id)">
              <img v-if="user.photo" class="block w-5 h-5 mr-2 -my-2 rounded-full" :src="user.photo" />
              {{ user.name }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('customers.edit', user.id)" tabindex="-1">
              {{ user.email }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('customers.edit', user.id)" tabindex="-1">
              {{ user.phone }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('customers.edit', user.id)" tabindex="-1">
              {{ __(user.city) }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('customers.edit', user.id)" tabindex="-1">
              {{ campusLabel(user.campus) }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('customers.edit', user.id)" tabindex="-1">
              {{ __(user.created_at) }}
              </Link>
            </td>
            <td class="w-px border-t">
              <Link class="flex items-center px-4" :href="route('customers.edit', user.id)" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
              </Link>
            </td>
          </tr>
          <tr v-if="users.data.length === 0">
            <td class="px-6 py-4 border-t" colspan="6">{{ __('No customers found.') }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination class="mt-6" :links="users.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'

export default {
  components: {
    SearchInput,
    Head,
    Icon,
    Link,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    users: Object,
    campusOptions: Array,
    awardingBodyOptions: Array,
    awardingBodyCampus: String,
    counts: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        campus: this.filters.campus || null,
        awarding_body: this.filters.awarding_body || null,
      },
    }
  },
  computed: {
    // "All" first, then one tab per campus - except the campus that is split by awarding
    // body, which contributes one tab per awarding body instead of a tab of its own. Both
    // lists come from config/campus.php, so adding an entry there is enough to add a tab.
    campusTabs() {
      const campusTabs = this.campusOptions
        .filter(option => option.value !== this.awardingBodyCampus)
        .map(option => ({
          key: `campus:${option.value}`,
          label: option.label,
          campus: option.value,
          awarding_body: null,
          count: this.counts.campus[option.value],
        }))

      const awardingBodyTabs = this.awardingBodyOptions.map(option => ({
        key: `awarding-body:${option.value}`,
        label: option.label,
        campus: this.awardingBodyCampus,
        awarding_body: option.value,
        count: this.counts.awarding_body[option.value],
      }))

      const allTab = { key: 'all', label: 'All', campus: null, awarding_body: null, count: this.counts.all }

      return [allTab, ...campusTabs, ...awardingBodyTabs]
    },
    campusLabelsByValue() {
      return this.campusOptions.reduce((labels, option) => {
        labels[option.value] = option.label
        return labels
      }, {})
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(this.route('customers'), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    // Writing to the form lets the existing watcher issue the Inertia request, so the
    // active tab and the search box stay combined in a single query. Replacing the object
    // rather than mutating it keeps the campus/awarding body pair a single change.
    filterByTab(tab) {
      this.form = { ...this.form, campus: tab.campus, awarding_body: tab.awarding_body }
    },
    isActiveTab(tab) {
      return this.form.campus === tab.campus && this.form.awarding_body === tab.awarding_body
    },
    campusTabClass(tab) {
      const base = 'inline-flex items-center px-4 py-3 -mb-px text-sm font-medium border-b-2 focus:outline-none'
      return this.isActiveTab(tab)
        ? `${base} text-indigo-600 border-indigo-600`
        : `${base} text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300`
    },
    campusTabCountClass(tab) {
      const base = 'ml-2 px-2 py-0.5 text-xs font-semibold rounded-full'
      return this.isActiveTab(tab) ? `${base} bg-indigo-100 text-indigo-800` : `${base} bg-gray-100 text-gray-600`
    },
    // Falls back to the stored value for a campus that has since left the config, and to
    // a dash when the student has none.
    campusLabel(campus) {
      if (!campus) {
        return '—'
      }
      return this.campusLabelsByValue[campus] ? this.__(this.campusLabelsByValue[campus]) : campus
    },
    reset() {
      this.form = mapValues(this.form, () => null)
    },
  },
}
</script>
